import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { injectIntl, FormattedMessage } from 'react-intl';
import isEmpty from 'lodash/isEmpty';
import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import includes from 'lodash/includes';
import { InView } from 'react-intersection-observer';
import classNames from 'classnames';
import { Link } from 'react-router-dom';

import { currencyCode } from 'Helpers/Geo/data';
import { fetchGiftCards } from 'Redux/ducks/algolia-calls';
import Backtotopbutton from 'Components/Utility/Mobile/Backtotopbutton';
import Breadcrumbs from 'Components/Utility/SEO/Breadcrumbs';
import Loading from 'Components/Utility/Loading';
import Meta from 'Components/Utility/SEO/Meta';
import HitCard from 'Components/Cards/HitCard';
import PriceContainer from 'Containers/Utility/Price/PriceContainer';
import SkeletonHitCard from 'Components/Cards/SkeletonHitCard';
import sortByName from 'Helpers/Sort/SortByName';
import messages from 'Helpers/SEO/GiftCardStrings';
import NoResults from 'Components/NotFound/NoResults';

function GiftCards({ intl, match: { params } }) {
  const [hits, setHits] = useState(null);
  const [sort, setSort] = useState('name');
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    const fetchData = async () => {
      const data = await fetchGiftCards();
      setHits(data.hits);
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (params.filter) {
      if (params.filter === 'nintendo') {
        setFilter('switch');
      } else if (params.filter === 'ea-play') {
        setFilter('eaplay');
      } else {
        setFilter(params.filter);
      }
    }
  }, [params.filter]);

  // null is the default state and implies component hasn't loaded
  if (hits === null) return <Loading />;

  const { formatMessage } = intl;

  let filterUrl = filter;
  if (filter === 'eaplay') filterUrl = 'ea-play';
  if (filter === 'switch') filterUrl = 'nintendo';

  let filteredHits = [];
  if (filter === 'all') {
    if (sort === 'topSellers') filteredHits = hits;
    if (sort === 'name') filteredHits = sortByName(cloneDeep(hits));
    if (sort === 'price') filteredHits = cloneDeep(hits).sort((a, b) => get(a, `price.${currencyCode}`, 0) - get(b, `price.${currencyCode}`, 0));
    if (sort === 'priceHighLow') filteredHits = cloneDeep(hits).sort((a, b) => get(b, `price.${currencyCode}`, 0) - get(a, `price.${currencyCode}`, 0));
    if (sort === 'topDiscount') filteredHits = cloneDeep(hits).sort((a, b) => get(b, 'discount_percent', 0) - get(a, 'discount_percent', 0));
  }
  if (filter !== 'all') {
    if (sort === 'topSellers') filteredHits = cloneDeep(hits).filter(hit => hit.drm.includes(filter));
    if (sort === 'name') filteredHits = sortByName(cloneDeep(hits).filter(hit => hit.drm.includes(filter)));
    if (sort === 'price') filteredHits = cloneDeep(hits).filter(hit => hit.drm.includes(filter)).sort((a, b) => get(a, `price.${currencyCode}`, 0) - get(b, `price.${currencyCode}`, 0));
    if (sort === 'priceHighLow') filteredHits = cloneDeep(hits).filter(hit => hit.drm.includes(filter)).sort((a, b) => get(b, `price.${currencyCode}`, 0) - get(a, `price.${currencyCode}`, 0));
    if (sort === 'topDiscount') filteredHits = cloneDeep(hits).filter(hit => hit.drm.includes(filter)).sort((a, b) => get(b, 'discount_percent', 0) - get(a, 'discount_percent', 0));
  }

  const crumbs = [
    { label: formatMessage(messages.title), link: '/gift-cards' },
  ];

  if (filter !== 'all') {
    crumbs.push({
      label: formatMessage(messages[`${filter}Title`]),
      link: `/gift-cards/${filterUrl}`,
    });
  }

  const giftCardTypes = [];
  hits.forEach((hit) => {
    if (!includes(giftCardTypes, hit.drm[0])) {
      giftCardTypes.push(hit.drm[0]);
    }
  });

  return (
    <main className="GiftCardsPage">
      {filter === 'all' ?
        <Meta
          title={`${formatMessage(messages.fullTitle)}`}
          description={formatMessage(messages.desc)}
          url="gift-cards"
          hits={hits}
          pageType="CollectionPage"
        />
        :
        <Meta
          brandTitle
          title={`${formatMessage(messages[`${filter}Title`])}`}
          description={`${formatMessage(messages[`${filter}SeoBlurb`])}`}
          url={`gift-cards/${filterUrl}`}
          hits={hits}
          pageType="CollectionPage"
        />
      }

      <Breadcrumbs crumbs={crumbs} />

      <div className="pt-5 container">
        <div className="title-container">
          <InView>
            {({ inView, ref }) =>
              <div ref={ref}>
                <h1>
                  {filter === 'all' ? formatMessage(messages.title) : formatMessage(messages[`${filter}Title`])}
                </h1>
                {!inView &&
                  <div className="d-block d-sm-none">
                    <Backtotopbutton />
                  </div>
                }
              </div>
            }
          </InView>

          {(filter === 'all' || filter === 'eaplay') &&
            <p className="seo-blurb-text mb-4">{formatMessage(messages.seoBlurb)}</p>
          }
          {filter === 'switch' &&
            <p className="seo-blurb-text mb-4">{formatMessage(messages.switchSeoBlurb)}</p>
          }
          {filter === 'playstation' &&
            <p className="seo-blurb-text mb-4">{formatMessage(messages.playstationSeoBlurb)}</p>
          }
          {filter === 'roblox' &&
            <p className="seo-blurb-text mb-4">{formatMessage(messages.robloxSeoBlurb)}</p>
          }
          {filter === 'razer' &&
            <p className="seo-blurb-text mb-4">{formatMessage(messages.razerSeoBlurb)}</p>
          }
          {filter === 'xbox' &&
            <p className="seo-blurb-text mb-4">{formatMessage(messages.xboxSeoBlurb)}</p>
          }

          <nav className="categories-and-sortby-section">
            <div className="bundle-pill-filters-container d-none d-lg-flex">
              <Link to="/gift-cards" className={classNames('pill-filter-container', { selected: filter === 'all' })} onClick={() => { setFilter('all'); }}>
                <FormattedMessage id="giftcards.filter.all" defaultMessage="All Gift Cards" />
              </Link>
              {includes(giftCardTypes, 'eaplay') &&
                <Link to="/gift-cards/ea-play" className={classNames('pill-filter-container', { selected: filter === 'eaplay' })} onClick={() => { setFilter('eaplay'); }}>
                  <FormattedMessage id="giftcards.filter.eaplay" defaultMessage="EA Play" />
                </Link>
              }
              {includes(giftCardTypes, 'switch') &&
                <Link to="/gift-cards/nintendo" className={classNames('pill-filter-container', { selected: filter === 'switch' })} onClick={() => { setFilter('switch'); }}>
                  <FormattedMessage id="giftcards.filter.nintendo" defaultMessage="Nintendo" />
                </Link>
              }
              {includes(giftCardTypes, 'playstation') &&
                <Link to="/gift-cards/playstation" className={classNames('pill-filter-container', { selected: filter === 'playstation' })} onClick={() => { setFilter('playstation'); }}>
                  <FormattedMessage id="giftcards.filter.playstation" defaultMessage="PlayStation" />
                </Link>
              }
              {includes(giftCardTypes, 'razer') &&
                <Link to="/gift-cards/razer" className={classNames('pill-filter-container', { selected: filter === 'razer' })} onClick={() => { setFilter('razer'); }}>
                  <FormattedMessage id="giftcards.filter.razer" defaultMessage="Razer" />
                </Link>
              }
              {includes(giftCardTypes, 'roblox') &&
                <Link to="/gift-cards/roblox" className={classNames('pill-filter-container', { selected: filter === 'roblox' })} onClick={() => { setFilter('roblox'); }}>
                  <FormattedMessage id="giftcards.filter.roblox" defaultMessage="Roblox" />
                </Link>
              }
              {includes(giftCardTypes, 'xbox') &&
                <Link to="/gift-cards/xbox" className={classNames('pill-filter-container', { selected: filter === 'xbox' })} onClick={() => { setFilter('xbox'); }}>
                  <FormattedMessage id="giftcards.filter.xbox" defaultMessage="Xbox" />
                </Link>
              }
            </div>

            <div className="filters-container">
              <div className="mr-3"><FormattedMessage id="giftcards.sortby" defaultMessage="Sort by" />:</div>
              <div className="dropdown-container sort-by">
                <select value={sort} onChange={e => setSort(e.target.value)}>
                  <option value="name">{formatMessage(messages.name)}</option>
                  <option value="topSellers">{formatMessage(messages.topSellers)}</option>
                  <option value="price">{formatMessage(messages.pricelowtohigh)}</option>
                  <option value="priceHighLow">{formatMessage(messages.pricehightolow)}</option>
                  <option value="topDiscount">{formatMessage(messages.topDiscount)}</option>
                </select>
              </div>
            </div>
          </nav>

          {filteredHits === null &&
            <div className="row">
              <SkeletonHitCard
                numberOfCards={36}
                lgSize={4}
              />
            </div>
          }


          {filteredHits && filteredHits.length === 0 &&
            <NoResults pageName={formatMessage(messages.title)} />
          }

          {!isEmpty(filteredHits) &&
            <ul className="HitCardsRow row">
              {filteredHits.map(hit =>
                <HitCard
                  key={hit.objectID}
                  hit={hit}
                  lgSize={4}
                  hitCardContainerTag="li"
                  hitCardNameTag="h2"
                >
                  <PriceContainer
                    discountPercent={hit.discount_percent}
                    price={hit.price}
                    tiered={hit.tiered}
                    slug={hit.slug}
                    hideDiscount={hit.hide_discount}
                    payWhatYouWant={hit.pay_what_you_want}
                  />
                </HitCard>,
              )}
            </ul>
          }

        </div>
      </div>
    </main>
  );
}

GiftCards.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      type: PropTypes.string,
    }),
  }),
};

GiftCards.defaultProps = {
  match: {
    params: {
      type: '',
    },
  },
};

export default injectIntl(GiftCards);
